import { toFlexibleNumber } from "util/flexibleNumber";
import { preprocessValueForTextField } from "util/preprocessValueForTextField";

/**
 * Contract for the shared Document-AI review/confirm grid.
 *
 * Nothing here knows what the reviewed values mean. A caller describes its
 * fields with an `ExtractionReviewSchema`, hands over an `ExtractionResult`
 * from whichever extractor produced it, and receives the reviewed values back
 * through its own commit handler. Waste profiles, bills of lading, invoices,
 * and data-capture ingests all express themselves in these terms, so the grid
 * itself carries no feature-specific logic.
 */

/** A value the grid can hold once it has been normalized for the caller. */
export type ExtractionFieldValue = string | number | boolean | null;

/**
 * A value while the reviewer is editing it. Numbers stay strings in the draft
 * so intermediate input ("-", "1.") survives keystrokes instead of collapsing
 * to NaN; `fromDraftValue` converts on commit.
 */
export type ExtractionDraftValue = string | boolean | null;

/** How a field is edited, and therefore how its draft converts back. */
export type ExtractionFieldKind =
  | "text"
  | "multiline"
  | "number"
  | "select"
  | "boolean"
  | "triBoolean";

/**
 * Yes / No / unknown, for a field where "the document didn't say" is a different
 * answer from "no".
 *
 * A checkbox can't express that: an unchecked box commits `false`, which reads as
 * the document having asserted a negative. Hazard characteristics are the case
 * this exists for — a profile sheet silent on reactivity must not pre-fill
 * `isReactive: false`, because the column's `false` default would make that
 * indistinguishable from a vendor saying so.
 */
export const TRI_BOOLEAN_OPTIONS: readonly ExtractionFieldOption[] = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export interface ExtractionFieldOption {
  label: string;
  value: string;
}

export interface ExtractionFieldSpec {
  /** Identifies the field in `ExtractionResult.values` and in confidence keys. */
  key: string;
  label: string;
  kind: ExtractionFieldKind;
  /** Required for `kind: "select"`; ignored otherwise. */
  options?: readonly ExtractionFieldOption[];
  /** Rendered inside the input as a suffix, e.g. "°F" or "%". */
  unit?: string;
  helperText?: string;
  /**
   * Why leaving this field blank matters, shown as the field's warning icon
   * while the draft is blank. Not validation: nothing blocks the commit, and the
   * field is not an attention target.
   */
  emptyWarning?: string;
  /**
   * How much of the row the field takes on a wide screen. Half by default.
   *
   * Fields tile in order, so this is what keeps a group together: a pH low and
   * high that do not tile onto one row end up on separate rows either side of an
   * unrelated field, and a reviewer comparing them against a printed range has to
   * hunt for the second half. Widths that divide the row exactly — three thirds,
   * two halves — put a group on its own line without needing a grouping concept.
   */
  width?: "third" | "half" | "full";
}

/** A titled block of single-value fields. */
export interface ExtractionSectionSpec {
  key: string;
  title?: string;
  fields: readonly ExtractionFieldSpec[];
}

/**
 * A list the extractor can return many of — an SDS composition table, a
 * bill-of-lading line list. Rendered as repeated editable rows the reviewer can
 * add to and remove from, never truncated to the first.
 */
export interface ExtractionRowGroupSpec {
  key: string;
  title: string;
  columns: readonly ExtractionFieldSpec[];
  /** Singular noun for row-level controls, e.g. "constituent". */
  rowNoun: string;
  /**
   * Which columns say that two rows are the same thing, for pairing a reading
   * against `ExtractionRecordValues.rows`. Every column by default.
   *
   * A constituent is the same constituent at a different percentage, so its
   * identity is the name and CAS number and not the range — without that, a
   * sheet restating a chemical at a new percentage reads as a row the record
   * does not have, and the reviewer is asked about an addition instead of a
   * change.
   */
  identityColumns?: readonly string[];
}

export interface ExtractionReviewSchema {
  sections: readonly ExtractionSectionSpec[];
  rowGroups?: readonly ExtractionRowGroupSpec[];
}

/** One field's extraction confidence, 0–1. */
export interface ExtractionFieldConfidence {
  /** A field key, or the `groupKey.index.columnKey` form for a row cell. */
  field: string;
  score: number;
}

/** What an extractor produced, before review. */
export interface ExtractionResult {
  /** Values to seed the form with, keyed by `ExtractionFieldSpec.key`. */
  values: Readonly<Record<string, ExtractionFieldValue>>;
  /** Rows to seed the form with, keyed by `ExtractionRowGroupSpec.key`. */
  rows?: Readonly<Record<string, readonly Readonly<Record<string, ExtractionFieldValue>>[]>>;
  /**
   * What the extractor itself read, when that is not what `values` seeds.
   *
   * The two diverge as soon as a caller seeds from anything else — a correction
   * someone saved on a previous pass, or a value already on the record. Without
   * this the reviewer is shown their own earlier answer labelled as the
   * extraction, and the model's actual reading is gone from the screen: they
   * cannot see what it said, and cannot put it back.
   *
   * Supplying it means "this is the extractor's reading for every field" — a key
   * that is absent here is one the extractor produced nothing for, even if
   * `values` seeds it. Omit the whole thing and `values` is treated as the
   * extractor's own output, which is the right default for a first review.
   */
  extractedValues?: Readonly<Record<string, ExtractionFieldValue>>;
  extractedRows?: Readonly<
    Record<string, readonly Readonly<Record<string, ExtractionFieldValue>>[]>
  >;
  confidence?: readonly ExtractionFieldConfidence[];
}

/** The extractor's own reading, which is `values`/`rows` unless a caller says otherwise. */
export function extractedBaseline(extraction: ExtractionResult): {
  values: ExtractionResult["values"];
  rows: NonNullable<ExtractionResult["rows"]>;
} {
  return {
    values: extraction.extractedValues ?? extraction.values,
    rows: extraction.extractedRows ?? extraction.rows ?? {},
  };
}

/**
 * What the record the review will be written over currently holds, in the same
 * schema-keyed terms as everything else the grid takes.
 *
 * Supplied by the caller and never interpreted: the grid compares it against
 * what the form holds and flags where the two disagree, so a value about to be
 * replaced is something the reviewer resolves rather than something the grid
 * decides for them. A caller that supplies nothing gets exactly the behaviour it
 * had before this existed.
 *
 * This is not part of the reading, which is why it is not a member of
 * `ExtractionResult`: an extractor produced none of it.
 */
export interface ExtractionRecordValues {
  values?: Readonly<Record<string, ExtractionFieldValue>>;
  rows?: Readonly<Record<string, readonly Readonly<Record<string, ExtractionFieldValue>>[]>>;
}

/**
 * Where a row in the form came from.
 *
 * `record` is the one this exists for: a row the record holds that the reading
 * has no counterpart for. It carries no `extractedIndex` — the model produced no
 * reading of it — which on its own is indistinguishable from a row the reviewer
 * typed, and the two want different treatment. A record row is flagged as
 * something to keep or drop deliberately; a row someone just added is not.
 */
export type ExtractionRowOrigin = "extraction" | "record" | "added";

/**
 * One reviewed row of a repeated group, with the extraction row behind it.
 *
 * `extractedIndex` is the row's position in the extraction result, or null on a
 * row the reviewer added. It travels with the row because a caller recording
 * what the reviewer changed has to say *which* extracted row a value belongs to,
 * and the reviewed list's own position is not that: deleting one row renumbers
 * every row after it, so a correction keyed by list position is recorded against
 * a different constituent than the reviewer edited.
 */
export interface ExtractionReviewedRow {
  extractedIndex: number | null;
  values: Record<string, ExtractionFieldValue>;
}

/** What the reviewer confirmed, handed to the caller's commit handler. */
export interface ExtractionReviewValues {
  values: Record<string, ExtractionFieldValue>;
  rows: Record<string, ExtractionReviewedRow[]>;
}

/**
 * Confidence key for one cell of a repeated row, e.g. `constituents[0].casNumber`
 * — the same shape the extraction pipeline derives from an extractor's schema, so
 * per-field confidence passes through to the grid without a translation step that
 * could silently mis-key.
 *
 * Indexed by the row's position in the extraction result, not its current
 * position in the grid, so adding or removing rows during review never re-points
 * a score at a different cell.
 */
export function rowConfidenceKey(
  groupKey: string,
  extractedRowIndex: number,
  columnKey: string
): string {
  return `${groupKey}[${extractedRowIndex}].${columnKey}`;
}

/**
 * Turn a caller value into its editable draft form. Numbers go through the
 * shared text-field preprocessor, which keeps a very small value (a trace
 * constituent percentage, a specific gravity) out of scientific notation —
 * `String(7.6e-11)` would put "7.6e-11" in front of the reviewer.
 */
export function toDraftValue(
  spec: ExtractionFieldSpec,
  value: ExtractionFieldValue | undefined
): ExtractionDraftValue {
  if (value == null) return null;
  // A tri-state is edited as a select, so its draft is the option value rather
  // than a boolean; null stays null and means "not answered".
  if (spec.kind === "triBoolean") return value === true ? "true" : "false";
  if (typeof value === "boolean") return value;
  return preprocessValueForTextField({
    value,
    defaultValue: undefined,
    // Only consulted for the phone-number path, which this never takes.
    onChange: () => {},
  });
}

/** Turn a draft back into the caller's value type, per the field's kind. */
export function fromDraftValue(
  spec: ExtractionFieldSpec,
  draft: ExtractionDraftValue | undefined
): ExtractionFieldValue {
  if (spec.kind === "boolean") return draft === true;
  if (typeof draft === "boolean") return draft;
  if (draft == null || draft.trim() === "") return null;
  // Unlike a checkbox, an unanswered tri-state stays null — handled by the blank
  // check above rather than collapsing to false.
  if (spec.kind === "triBoolean") return draft === "true";
  // A number only when the whole string is one. `safeParseFloat` wraps
  // `parseFloat`, which takes a numeric prefix — so "12abc" would come back as
  // 12, replacing what someone typed with a value they never entered. Handing the
  // raw string on instead lets the caller's own validation refuse it by name,
  // which is what `FlexibleFloat` inputs and the engine's `.number()` rule do.
  if (spec.kind === "number") return toFlexibleNumber(draft);
  return draft;
}

/** True when a draft holds nothing the reviewer could commit. */
export function isBlankDraft(draft: ExtractionDraftValue | undefined): boolean {
  if (typeof draft === "boolean") return draft === false;
  return draft == null || draft.trim() === "";
}

/**
 * True when nothing was ever read here — as distinct from `isBlankDraft`, which
 * asks whether there is a value worth committing.
 *
 * The two differ on exactly one draft, and it matters: a checkbox read as `false`
 * commits nothing, so it is blank, but it *is* a reading. Anything offering that
 * reading back to a reviewer — an offered value, the sparkle — has to use this
 * one, or a reviewer who ticks a box the extractor read as unticked is left with
 * no way back to it.
 */
export function isUnreadDraft(draft: ExtractionDraftValue | undefined): boolean {
  if (typeof draft === "boolean") return false;
  return draft == null || draft.trim() === "";
}
